import { useEffect, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { buildWorkspacePlanSettingsHref } from '@/features/organisations/utils/organisationSettingsRoute';
import {
  ProjectCsvMappingSection,
  ProjectCoverageMetricThresholdSection,
  ProjectDataWipeSection,
  ProjectMetricThresholdSection,
  ProjectModuleDataWipeSection,
  ProjectModuleOverviewPanels,
  ProjectSettingsDetailsRail,
  ProjectTestDataControlsSection,
  ProjectUploadTokensSection,
} from '@/features/projects/components';
import {
  ProjectApiKeySection,
  ProjectDetailsSection,
} from '@/features/projects/components/ProjectSettingsSections';
import { resolveProjectSettingsTabFromHash } from '@/features/projects/utils/projectSettingsRoute';
import { getToastMessage } from '@/shared/copy/toasts';
import { s } from '@/theme/tokens';
import { Box, Stack } from '@/ui';
import { settingsPageTypographySx } from '@/ui/composites/settingsPageTypographySx';
import { SettingsTabbedHeader } from '@/ui/composites/SettingsTabbedHeader';
import { Section } from '@/ui/layout/Section';
import { PageShell } from '@/ui/primitives/PageShell';
import type {
  ProjectDataWipeSectionProps,
  ProjectTestDataControlsSectionProps,
} from '@/features/projects/components';
import type {
  ProjectApiKeySectionProps,
  ProjectDetailsSectionProps,
} from '@/features/projects/components/ProjectSettingsSections';
import type { ProjectSettingsViewModel } from '@/features/projects/hooks/useProjectSettingsViewModel';
import type { ReactElement } from 'react';

type ProjectSettingsContentViewModel = Omit<
  ProjectSettingsViewModel,
  | 'nameValue'
  | 'setNameValue'
  | 'nameDirty'
  | 'setNameDirty'
  | 'slugValue'
  | 'setSlugValue'
  | 'badgeEnabledValue'
  | 'setBadgeEnabledValue'
  | 'badgeCharsValue'
  | 'setBadgeCharsValue'
  | 'badgeColorValue'
  | 'setBadgeColorValue'
  | 'apiKeyValue'
  | 'setApiKeyValue'
  | 'revokeOpen'
  | 'setRevokeOpen'
  | 'apiKeyError'
  | 'rotateProjectApiKey'
  | 'testProjectPipelineWebhook'
  | 'deleteProject'
  | 'wipeProjectData'
>;

type ProjectSettingsContentProps = {
  viewModel: ProjectSettingsContentViewModel;
  projectDetailsSection: ProjectDetailsSectionProps;
  projectApiKeySection: ProjectApiKeySectionProps;
  projectTestDataControlsSection: ProjectTestDataControlsSectionProps;
  projectDataWipeSection: ProjectDataWipeSectionProps;
  hideCodeSettings: boolean;
  hideDeliverySettings: boolean;
  hideUploadTokenSettings: boolean;
  lockUploadSettings: boolean;
  lockUploadTokenSettings: boolean;
  hidePipelineWebhookSettings: boolean;
  hidePipelineMetricThresholds: boolean;
  lockMetricThresholds: boolean;
};

type ProjectSettingsTabId = 'general' | 'delivery' | 'code' | 'test' | 'operations';

const projectSettingsSectionGap = 'xl' as const;
const projectSettingsMainContentWidth = '600px';
const projectSettingsModuleRailWidth = '240px';
const projectSettingsModuleOuterWidth = `calc(${projectSettingsMainContentWidth} + ${s('xl')} + ${projectSettingsModuleRailWidth})`;
const projectSettingsModuleHorizontalOffset = `calc((${projectSettingsModuleRailWidth} + ${s('xl')}) / 2)`;
const projectSettingsCompactLeftInset = s('sm');
const projectSettingsCompactRightInset = s('md');
const isProjectSettingsTabId = (value: string | null): value is ProjectSettingsTabId =>
  value === 'general' ||
  value === 'delivery' ||
  value === 'code' ||
  value === 'test' ||
  value === 'operations';

const thresholdPanelsGridSx = {
  display: 'grid',
  gridTemplateColumns: {
    xs: 'minmax(0, 1fr)',
    xl: '1fr',
  },
  gap: s(projectSettingsSectionGap),
  alignItems: 'start',
} as const;

const projectSettingsModuleLayoutSx = {
  display: 'grid',
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  gridTemplateColumns: {
    xs: 'minmax(0, 1fr)',
    md: `${projectSettingsMainContentWidth} minmax(0, ${projectSettingsModuleRailWidth})`,
  },
  columnGap: s('xl'),
  rowGap: s(projectSettingsSectionGap),
  alignItems: 'start',
  justifyContent: {
    xs: 'stretch',
    md: 'start',
    xl: 'center',
  },
  '& > *': {
    minWidth: 0,
  },
} as const;

export const ProjectSettingsContent = ({
  viewModel,
  projectDetailsSection,
  projectApiKeySection,
  projectTestDataControlsSection,
  projectDataWipeSection,
  hideCodeSettings,
  hideDeliverySettings,
  hideUploadTokenSettings,
  lockUploadSettings,
  lockUploadTokenSettings,
  hidePipelineWebhookSettings,
  hidePipelineMetricThresholds,
  lockMetricThresholds,
}: ProjectSettingsContentProps): ReactElement => {
  const resolvedProject = viewModel.project ?? null;
  const testingMetricsEnabled = resolvedProject?.testDataControls.testingMetricsEnabled ?? true;
  const metricThresholdsDisabled = lockMetricThresholds || !testingMetricsEnabled;
  const metricThresholdsDisabledReason = lockMetricThresholds
    ? 'Metric threshold editing is not available on Free plans.'
    : 'Enable Show Metrics to configure.';
  const planSettingsHref =
    viewModel.selectedOrganisationId !== null
      ? buildWorkspacePlanSettingsHref(viewModel.selectedOrganisationId)
      : '/plan-settings';
  const uploadTokenSettingsDisabledHref = lockUploadTokenSettings ? planSettingsHref : undefined;
  const uploadSettingsDisabledHref = lockUploadSettings ? planSettingsHref : undefined;
  const metricThresholdsDisabledHref = lockMetricThresholds ? planSettingsHref : undefined;
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const showDeliveryTab = !hideDeliverySettings;
  const showCodeTab = !hideCodeSettings;
  const showOperationsTab = !hidePipelineWebhookSettings || !hidePipelineMetricThresholds;
  const requestedTab = searchParams.get('tab');
  const hashRequestedTab = resolveProjectSettingsTabFromHash(location.hash);
  const projectSettingsTabDefinitions = useMemo(() => {
    const tabs: Array<{ value: ProjectSettingsTabId; label: string }> = [
      { value: 'general', label: 'General' },
      { value: 'test', label: 'Test' },
    ];
    if (showDeliveryTab) {
      tabs.push({ value: 'delivery', label: 'Delivery' });
    }
    if (showCodeTab) {
      tabs.push({ value: 'code', label: 'Code' });
    }
    if (showOperationsTab) {
      tabs.push({ value: 'operations', label: 'Operations' });
    }
    return tabs;
  }, [showCodeTab, showDeliveryTab, showOperationsTab]);
  const activeTab = useMemo<ProjectSettingsTabId>(() => {
    if (isProjectSettingsTabId(requestedTab)) {
      if (requestedTab === 'delivery' && !showDeliveryTab) {
        return 'general';
      }
      if (requestedTab === 'code' && !showCodeTab) {
        return 'general';
      }
      if (requestedTab === 'operations' && !showOperationsTab) {
        return 'general';
      }
      return requestedTab;
    }

    if (hashRequestedTab === 'operations' && !showOperationsTab) {
      return 'general';
    }
    if (hashRequestedTab !== null) {
      return hashRequestedTab;
    }

    return 'general';
  }, [hashRequestedTab, requestedTab, showCodeTab, showDeliveryTab, showOperationsTab]);

  useEffect(() => {
    if (requestedTab !== null && activeTab === 'general' && requestedTab !== 'general') {
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.delete('tab');
      setSearchParams(nextSearchParams, { replace: true });
    }
  }, [activeTab, requestedTab, searchParams, setSearchParams]);

  const copyProjectId = (): void => {
    if (!resolvedProject) {
      return;
    }
    void navigator.clipboard.writeText(resolvedProject.id);
    viewModel.notify({
      message: getToastMessage('clipboard.copied', { label: 'Project ID' }),
      tone: 'green',
    });
  };

  return (
    <PageShell maxWidth="lg" paddingX="lg" paddingY="xl" paddingBottom="xl">
      <Stack gap="xl" sx={settingsPageTypographySx}>
        <Section gap="sm">
          <Box
            sx={{
              width: '100%',
              maxWidth: {
                xs: '100%',
                md: projectSettingsModuleOuterWidth,
              },
              mx: { xs: 0, md: 'auto' },
              pl: { xs: projectSettingsCompactLeftInset, md: 0 },
              pr: { xs: projectSettingsCompactRightInset, md: 0 },
              position: 'relative',
              left: { xl: projectSettingsModuleHorizontalOffset },
              boxSizing: 'border-box',
              minWidth: 0,
            }}
          >
            <Box sx={{ width: '100%', maxWidth: projectSettingsMainContentWidth }}>
              <SettingsTabbedHeader
                title="Project Settings"
                subtitle="Manage project and module configuration."
                activeTab={activeTab}
                tabs={projectSettingsTabDefinitions}
                tabsAriaLabel="Project settings sections"
                onTabChange={(nextValue) => {
                  const nextTabId = isProjectSettingsTabId(nextValue) ? nextValue : null;
                  if (!nextTabId) {
                    return;
                  }
                  const nextSearchParams = new URLSearchParams(searchParams);
                  if (nextTabId === 'general') {
                    nextSearchParams.delete('tab');
                  } else {
                    nextSearchParams.set('tab', nextTabId);
                  }
                  setSearchParams(nextSearchParams, { replace: true });
                }}
              />
            </Box>
          </Box>
          {activeTab === 'general' ? (
            <Box
              sx={{
                width: '100%',
                maxWidth: {
                  xs: '100%',
                  md: projectSettingsModuleOuterWidth,
                },
                mx: { xs: 0, md: 'auto' },
                pl: { xs: projectSettingsCompactLeftInset, md: 0 },
                pr: { xs: projectSettingsCompactRightInset, md: 0 },
                position: 'relative',
                left: { xl: projectSettingsModuleHorizontalOffset },
                boxSizing: 'border-box',
                minWidth: 0,
              }}
            >
              <Box sx={projectSettingsModuleLayoutSx}>
                <Box
                  sx={{
                    gridColumn: { xs: '1', md: '1' },
                    width: '100%',
                    minWidth: 0,
                    maxWidth: projectSettingsMainContentWidth,
                    order: { xs: 2, md: 1 },
                  }}
                >
                  <ProjectDetailsSection {...projectDetailsSection} />
                </Box>
                <Box
                  sx={{
                    gridColumn: { xs: '1', md: '2' },
                    width: '100%',
                    minWidth: 0,
                    order: { xs: 1, md: 2 },
                  }}
                >
                  <ProjectSettingsDetailsRail
                    project={resolvedProject}
                    onCopyProjectId={copyProjectId}
                  />
                </Box>
                <Box
                  sx={{
                    gridColumn: { xs: '1', md: '1' },
                    width: '100%',
                    minWidth: 0,
                    maxWidth: projectSettingsMainContentWidth,
                    order: 3,
                  }}
                >
                  <ProjectDataWipeSection {...projectDataWipeSection} />
                </Box>
              </Box>
            </Box>
          ) : null}
          {activeTab === 'test' ? (
            <Box
              sx={{
                width: '100%',
                maxWidth: {
                  xs: '100%',
                  md: projectSettingsModuleOuterWidth,
                },
                mx: { xs: 0, md: 'auto' },
                pl: { xs: projectSettingsCompactLeftInset, md: 0 },
                pr: { xs: projectSettingsCompactRightInset, md: 0 },
                position: 'relative',
                left: { xl: projectSettingsModuleHorizontalOffset },
                boxSizing: 'border-box',
                minWidth: 0,
              }}
            >
              <Box sx={projectSettingsModuleLayoutSx}>
                <Box
                  sx={{
                    gridColumn: { xs: '1', md: '1' },
                    width: '100%',
                    maxWidth: projectSettingsMainContentWidth,
                    order: { xs: 2, md: 1 },
                  }}
                >
                  <ProjectTestDataControlsSection
                    {...projectTestDataControlsSection}
                    uploadSettingsDisabled={lockUploadSettings}
                    uploadSettingsDisabledHref={uploadSettingsDisabledHref}
                  />
                </Box>
                {resolvedProject ? (
                  <Box
                    sx={{
                      gridColumn: { xs: '1', md: '2' },
                      width: '100%',
                      order: { xs: 1, md: 2 },
                    }}
                  >
                    <ProjectModuleOverviewPanels
                      projectId={resolvedProject.id}
                      organisationId={
                        resolvedProject.organisationId ?? viewModel.selectedOrganisationId
                      }
                      modules={['test']}
                    />
                  </Box>
                ) : null}
                <Box
                  sx={{
                    gridColumn: { xs: '1', md: '1' },
                    width: '100%',
                    maxWidth: projectSettingsMainContentWidth,
                    order: 3,
                  }}
                >
                  <Stack gap={projectSettingsSectionGap}>
                    {resolvedProject && !hideUploadTokenSettings ? (
                      <ProjectUploadTokensSection
                        projectId={resolvedProject.id}
                        container="panel"
                        disabled={lockUploadTokenSettings}
                        disabledReason="Upload token management is not available on Free plans."
                        disabledHref={uploadTokenSettingsDisabledHref}
                      />
                    ) : null}
                    <Box>
                      <ProjectCsvMappingSection
                        projectId={resolvedProject?.id ?? null}
                        notify={viewModel.notify}
                      />
                    </Box>
                    <Box sx={thresholdPanelsGridSx}>
                      <Box>
                        <ProjectMetricThresholdSection
                          title="Execution Run Metric Thresholds"
                          description=""
                          successMessage="Saved execution run metric thresholds"
                          thresholdKey="executionRuns"
                          drafts={viewModel.executionRunThresholdDrafts}
                          setDrafts={viewModel.setExecutionRunThresholdDrafts}
                          preferredUnitsRef={viewModel.executionRunPreferredUnitsRef}
                          isValid={viewModel.executionRunThresholdsValid}
                          isPending={viewModel.updateProject.isPending}
                          gridSpacing="sm"
                          project={resolvedProject}
                          updateProject={viewModel.updateProject}
                          mutationError={viewModel.mutationError}
                          notify={viewModel.notify}
                          resetDrafts={viewModel.resetExecutionRunThresholdDrafts}
                          disabled={metricThresholdsDisabled}
                          disabledReason={metricThresholdsDisabledReason}
                          disabledHref={metricThresholdsDisabledHref}
                        />
                      </Box>
                      <ProjectMetricThresholdSection
                        title="Suite Metric Thresholds"
                        description=""
                        successMessage="Saved suite metric thresholds"
                        thresholdKey="suites"
                        drafts={viewModel.suiteThresholdDrafts}
                        setDrafts={viewModel.setSuiteThresholdDrafts}
                        preferredUnitsRef={viewModel.suitePreferredUnitsRef}
                        isValid={viewModel.suiteThresholdsValid}
                        isPending={viewModel.updateProject.isPending}
                        gridSpacing="sm"
                        project={resolvedProject}
                        updateProject={viewModel.updateProject}
                        mutationError={viewModel.mutationError}
                        notify={viewModel.notify}
                        resetDrafts={viewModel.resetSuiteThresholdDrafts}
                        disabled={metricThresholdsDisabled}
                        disabledReason={metricThresholdsDisabledReason}
                        disabledHref={metricThresholdsDisabledHref}
                      />
                      <ProjectMetricThresholdSection
                        title="Test Metric Thresholds"
                        description=""
                        successMessage="Saved test metric thresholds"
                        thresholdKey="tests"
                        drafts={viewModel.testThresholdDrafts}
                        setDrafts={viewModel.setTestThresholdDrafts}
                        preferredUnitsRef={viewModel.testPreferredUnitsRef}
                        isValid={viewModel.testThresholdsValid}
                        isPending={viewModel.updateProject.isPending}
                        gridSpacing="sm"
                        project={resolvedProject}
                        updateProject={viewModel.updateProject}
                        mutationError={viewModel.mutationError}
                        notify={viewModel.notify}
                        resetDrafts={viewModel.resetTestThresholdDrafts}
                        disabled={metricThresholdsDisabled}
                        disabledReason={metricThresholdsDisabledReason}
                        disabledHref={metricThresholdsDisabledHref}
                      />
                    </Box>
                    <ProjectModuleDataWipeSection
                      module="test"
                      project={resolvedProject}
                      wipeProjectData={projectDataWipeSection.wipeProjectData}
                      mutationError={projectDataWipeSection.mutationError}
                      notify={projectDataWipeSection.notify}
                    />
                  </Stack>
                </Box>
              </Box>
            </Box>
          ) : null}
          {showDeliveryTab && activeTab === 'delivery' ? (
            <Box
              sx={{
                width: '100%',
                maxWidth: {
                  xs: '100%',
                  md: projectSettingsModuleOuterWidth,
                },
                mx: { xs: 0, md: 'auto' },
                pl: { xs: projectSettingsCompactLeftInset, md: 0 },
                pr: { xs: projectSettingsCompactRightInset, md: 0 },
                position: 'relative',
                left: { xl: projectSettingsModuleHorizontalOffset },
                boxSizing: 'border-box',
                minWidth: 0,
              }}
            >
              <Box sx={projectSettingsModuleLayoutSx}>
                <Box
                  sx={{
                    gridColumn: { xs: '1', md: '1' },
                    width: '100%',
                    maxWidth: projectSettingsMainContentWidth,
                    order: { xs: 2, md: 1 },
                  }}
                >
                  <ProjectModuleDataWipeSection
                    module="delivery"
                    project={resolvedProject}
                    wipeProjectData={projectDataWipeSection.wipeProjectData}
                    mutationError={projectDataWipeSection.mutationError}
                    notify={projectDataWipeSection.notify}
                  />
                </Box>
                {resolvedProject ? (
                  <Box
                    sx={{
                      gridColumn: { xs: '1', md: '2' },
                      width: '100%',
                      order: { xs: 1, md: 2 },
                    }}
                  >
                    <ProjectModuleOverviewPanels
                      projectId={resolvedProject.id}
                      organisationId={
                        resolvedProject.organisationId ?? viewModel.selectedOrganisationId
                      }
                      modules={['delivery']}
                    />
                  </Box>
                ) : null}
              </Box>
            </Box>
          ) : null}
          {showCodeTab && activeTab === 'code' ? (
            <Box
              sx={{
                width: '100%',
                maxWidth: {
                  xs: '100%',
                  md: projectSettingsModuleOuterWidth,
                },
                mx: { xs: 0, md: 'auto' },
                pl: { xs: projectSettingsCompactLeftInset, md: 0 },
                pr: { xs: projectSettingsCompactRightInset, md: 0 },
                position: 'relative',
                left: { xl: projectSettingsModuleHorizontalOffset },
                boxSizing: 'border-box',
                minWidth: 0,
              }}
            >
              <Box sx={projectSettingsModuleLayoutSx}>
                <Box
                  sx={{
                    gridColumn: { xs: '1', md: '1' },
                    width: '100%',
                    maxWidth: projectSettingsMainContentWidth,
                    order: { xs: 2, md: 1 },
                  }}
                >
                  <Stack gap={projectSettingsSectionGap}>
                    <ProjectCoverageMetricThresholdSection
                      title="Coverage Runs Coverage Thresholds"
                      thresholdKey="coverageRuns"
                      successMessage="Saved coverage run metric thresholds"
                      project={resolvedProject}
                      isPending={viewModel.updateProject.isPending}
                      updateProject={viewModel.updateProject}
                      mutationError={viewModel.mutationError}
                      notify={viewModel.notify}
                    />
                    <ProjectCoverageMetricThresholdSection
                      title="Directories Coverage Thresholds"
                      thresholdKey="directories"
                      successMessage="Saved directory coverage metric thresholds"
                      project={resolvedProject}
                      isPending={viewModel.updateProject.isPending}
                      updateProject={viewModel.updateProject}
                      mutationError={viewModel.mutationError}
                      notify={viewModel.notify}
                    />
                    <ProjectCoverageMetricThresholdSection
                      title="Files Coverage Thresholds"
                      thresholdKey="files"
                      successMessage="Saved file coverage metric thresholds"
                      project={resolvedProject}
                      isPending={viewModel.updateProject.isPending}
                      updateProject={viewModel.updateProject}
                      mutationError={viewModel.mutationError}
                      notify={viewModel.notify}
                    />
                    <ProjectModuleDataWipeSection
                      module="code"
                      project={resolvedProject}
                      wipeProjectData={projectDataWipeSection.wipeProjectData}
                      mutationError={projectDataWipeSection.mutationError}
                      notify={projectDataWipeSection.notify}
                    />
                  </Stack>
                </Box>
                {resolvedProject ? (
                  <Box
                    sx={{
                      gridColumn: { xs: '1', md: '2' },
                      width: '100%',
                      order: { xs: 1, md: 2 },
                    }}
                  >
                    <ProjectModuleOverviewPanels
                      projectId={resolvedProject.id}
                      organisationId={
                        resolvedProject.organisationId ?? viewModel.selectedOrganisationId
                      }
                      modules={['code']}
                    />
                  </Box>
                ) : null}
              </Box>
            </Box>
          ) : null}
          <Box
            sx={{
              width: '100%',
              maxWidth: {
                xs: '100%',
                md: projectSettingsModuleOuterWidth,
              },
              mx: { xs: 0, md: 'auto' },
              pl: { xs: projectSettingsCompactLeftInset, md: 0 },
              pr: { xs: projectSettingsCompactRightInset, md: 0 },
              position: 'relative',
              left: { xl: projectSettingsModuleHorizontalOffset },
              boxSizing: 'border-box',
              minWidth: 0,
            }}
          >
            {activeTab === 'operations' ? (
              <Box sx={projectSettingsModuleLayoutSx}>
                <Box
                  sx={{
                    gridColumn: { xs: '1', xl: '1' },
                    width: '100%',
                    maxWidth: projectSettingsMainContentWidth,
                    order: { xs: 2, md: 1 },
                  }}
                >
                  <Stack gap={projectSettingsSectionGap}>
                    {!hidePipelineWebhookSettings ? (
                      <ProjectApiKeySection {...projectApiKeySection} />
                    ) : null}
                    {hidePipelineMetricThresholds ? null : (
                      <Box sx={thresholdPanelsGridSx}>
                        <ProjectMetricThresholdSection
                          title="Pipeline Metric Thresholds"
                          description=""
                          successMessage="Saved pipeline metric thresholds"
                          thresholdKey="pipelines"
                          drafts={viewModel.pipelineThresholdDrafts}
                          setDrafts={viewModel.setPipelineThresholdDrafts}
                          preferredUnitsRef={viewModel.pipelinePreferredUnitsRef}
                          isValid={viewModel.pipelineThresholdsValid}
                          isPending={viewModel.updateProject.isPending}
                          gridSpacing="sm"
                          project={resolvedProject}
                          updateProject={viewModel.updateProject}
                          mutationError={viewModel.mutationError}
                          notify={viewModel.notify}
                          resetDrafts={viewModel.resetPipelineThresholdDrafts}
                          disabled={metricThresholdsDisabled}
                          disabledReason={metricThresholdsDisabledReason}
                          disabledHref={metricThresholdsDisabledHref}
                        />
                        <ProjectMetricThresholdSection
                          title="Stage Metric Thresholds"
                          description=""
                          successMessage="Saved stage metric thresholds"
                          thresholdKey="stages"
                          drafts={viewModel.stageThresholdDrafts}
                          setDrafts={viewModel.setStageThresholdDrafts}
                          preferredUnitsRef={viewModel.stagePreferredUnitsRef}
                          isValid={viewModel.stageThresholdsValid}
                          isPending={viewModel.updateProject.isPending}
                          gridSpacing="sm"
                          project={resolvedProject}
                          updateProject={viewModel.updateProject}
                          mutationError={viewModel.mutationError}
                          notify={viewModel.notify}
                          resetDrafts={viewModel.resetStageThresholdDrafts}
                          disabled={metricThresholdsDisabled}
                          disabledReason={metricThresholdsDisabledReason}
                          disabledHref={metricThresholdsDisabledHref}
                        />
                        <ProjectMetricThresholdSection
                          title="Job Metric Thresholds"
                          description=""
                          successMessage="Saved job metric thresholds"
                          thresholdKey="jobs"
                          drafts={viewModel.jobThresholdDrafts}
                          setDrafts={viewModel.setJobThresholdDrafts}
                          preferredUnitsRef={viewModel.jobPreferredUnitsRef}
                          isValid={viewModel.jobThresholdsValid}
                          isPending={viewModel.updateProject.isPending}
                          gridSpacing="sm"
                          project={resolvedProject}
                          updateProject={viewModel.updateProject}
                          mutationError={viewModel.mutationError}
                          notify={viewModel.notify}
                          resetDrafts={viewModel.resetJobThresholdDrafts}
                          disabled={metricThresholdsDisabled}
                          disabledReason={metricThresholdsDisabledReason}
                          disabledHref={metricThresholdsDisabledHref}
                        />
                      </Box>
                    )}
                    <ProjectModuleDataWipeSection
                      module="operations"
                      project={resolvedProject}
                      wipeProjectData={projectDataWipeSection.wipeProjectData}
                      mutationError={projectDataWipeSection.mutationError}
                      notify={projectDataWipeSection.notify}
                    />
                  </Stack>
                </Box>
                {resolvedProject ? (
                  <Box
                    sx={{
                      gridColumn: { xs: '1', md: '2' },
                      width: '100%',
                      order: { xs: 1, md: 2 },
                    }}
                  >
                    <ProjectModuleOverviewPanels
                      projectId={resolvedProject.id}
                      organisationId={
                        resolvedProject.organisationId ?? viewModel.selectedOrganisationId
                      }
                      modules={['operations']}
                    />
                  </Box>
                ) : null}
              </Box>
            ) : null}
          </Box>
        </Section>
      </Stack>
    </PageShell>
  );
};
